import type { ExpenseStatus } from "./status";
import type { MoneyTotal } from "../currency";

export const EXPENSE_ATTACHMENT_ACCEPT =
  "application/pdf,image/jpeg,image/png,image/heic,image/heif,.heic,.heif";
export const RECEIPT_IMPORT_ACCEPT =
  "application/pdf,image/jpeg,image/png,image/heic,image/heif,.heic,.heif";
const RECEIPT_IMPORT_CONTENT_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
  "image/x-heic",
  "image/x-heif",
]);
const RECEIPT_IMPORT_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".heic", ".heif"];

export const isHeifContentType = (contentType: string) => {
  const normalized = contentType.toLowerCase();
  return normalized === "image/heic" || normalized === "image/heif" ||
    normalized === "image/x-heic" || normalized === "image/x-heif";
};

export const isExtractableReceiptFile = (file: File) => {
  const contentType = (file.type || "").toLowerCase();
  const filename = file.name.toLowerCase();
  return (
    RECEIPT_IMPORT_CONTENT_TYPES.has(contentType) ||
    RECEIPT_IMPORT_EXTENSIONS.some((extension) => filename.endsWith(extension))
  );
};

export const validateExpenseUploadFile = (file: File) => {
  if (file.size <= 0 || file.size > 20 * 1024 * 1024) {
    throw new Error("Attachment must be between 1 byte and 20 MB");
  }
  if (!isExtractableReceiptFile(file)) {
    throw new Error("Attachment must be a PDF, JPG, PNG, HEIC, or HEIF image");
  }
};
export const RECEIPT_IMPORT_CHANGED_EVENT =
  "vend.expense.receiptImportChanged";
export const EXPENSE_IMPORT_BATCH_CHANGED_EVENT =
  "vend.expense.importBatchChanged";

export type ExpenseCategory = {
  id: string;
  name: string;
  code: string;
  active: boolean;
  isDefault?: boolean;
  entryMode?: "user" | "system";
};
export type { ExpenseStatus } from "./status";
export type PaymentAccount = {
  id: string;
  label: string;
  type: PaymentAccountType;
  provider: string;
  walletProvider: string;
  brand: string;
  lastFour: string;
  active: boolean;
  createdFromBilling?: boolean;
  createdAt: string;
  updatedAt: string;
};
export type PaymentAccountType =
  | "card"
  | "bank_account"
  | "cash"
  | "check"
  | "digital_wallet"
  | "other";
export type ExpenseAttachment = {
  id: string;
  originalFilename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
  extraction?: ExpenseExtraction | null;
};
export const EXPENSE_SHIPMENT_CARRIERS = [
  "ups",
  "fedex",
  "usps",
  "dhl",
  "other",
] as const;
export type ExpenseShipmentCarrier = typeof EXPENSE_SHIPMENT_CARRIERS[number];
export const EXPENSE_SHIPMENT_STATUSES = [
  "unknown",
  "label_created",
  "in_transit",
  "out_for_delivery",
  "delivered",
  "exception",
  "returned",
] as const;
export type ExpenseShipmentStatus = typeof EXPENSE_SHIPMENT_STATUSES[number];
export type ExpenseShipmentEvent = {
  id: string;
  status: ExpenseShipmentStatus;
  carrierStatusCode: string;
  description: string;
  location: string;
  occurredAt: string;
};
export type ExpenseShipment = {
  id: string;
  carrier: ExpenseShipmentCarrier;
  carrierName: string;
  trackingNumber: string;
  status: ExpenseShipmentStatus;
  carrierStatusCode: string;
  carrierStatusText: string;
  shippedAt?: string | null;
  estimatedDeliveryAt?: string | null;
  deliveredAt?: string | null;
  receivedAt?: string | null;
  lastCheckedAt?: string | null;
  refreshAvailable: boolean;
  refreshAfter?: string | null;
  trackingUrl?: string | null;
  events: ExpenseShipmentEvent[];
  archivedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};
export type ExpenseReference = {
  id: string;
  name: string;
  email?: string | null;
};
export type ReceiptExtractionResult = {
  merchant_name?: string | null;
  expense_date?: string | null;
  subtotal?: number | string | null;
  shipping?: number | string | null;
  tax?: number | string | null;
  discount?: number | string | null;
  total?: number | string | null;
  currency?: string | null;
  receipt_number?: string | null;
  payment_method?: string | null;
  payment_brand?: string | null;
  payment_last_four?: string | null;
  merchant_street?: string | null;
  merchant_city?: string | null;
  merchant_state?: string | null;
  merchant_postal_code?: string | null;
  merchant_country?: string | null;
  merchant_address_raw?: string | null;
  store_number?: string | null;
  machine_identifier?: string | null;
  memo?: string | null;
  shipments?: Array<{
    carrier: ExpenseShipmentCarrier;
    carrier_name?: string | null;
    tracking_number: string;
    shipped_date?: string | null;
    received_date?: string | null;
  }>;
  category_code?: string | null;
  confidence?: number | null;
  printed_item_count?: number | null;
  line_items_complete?: boolean;
  items?: Array<{
    item_name: string;
    quantity: number | string;
    unit_price?: number | string | null;
    printed_line_total?: number | string | null;
    discount?: number | string | null;
    tax?: number | string | null;
    category_code?: string | null;
  }>;
};
export type ExpenseExtraction = {
  id: string;
  status: "queued" | "processing" | "needs_review" | "completed" | "failed";
  attempts: number;
  provider?: string;
  result?: ReceiptExtractionResult | null;
  errorCode?: string;
  errorMessage?: string;
};
export const extractionIsPending = (
  status: ExpenseExtraction["status"] | "uploaded",
) => status === "uploaded" || status === "queued" || status === "processing";
export const extractionCanBeReviewed = (
  status: ExpenseExtraction["status"],
) => status === "completed" || status === "needs_review";
export type ReceiptSuggestion = {
  id: string;
  label: string;
  reason: string;
  confidence: number;
};
export type ReceiptResolution = {
  category?: ReceiptSuggestion | null;
  paymentAccount?: ReceiptSuggestion | null;
  location?: ReceiptSuggestion | null;
  machine?: ReceiptSuggestion | null;
  lineItemCategoryIds: Array<string | null>;
};
export type ReceiptReview = {
  extraction: ExpenseExtraction;
  resolution: ReceiptResolution;
  duplicateWarning?: DuplicateExpenseWarning | null;
};
export type ReceiptImport = {
  id: string;
  originalFilename: string;
  contentType: string;
  sizeBytes: number;
  duplicateAttachmentId?: string | null;
  status: ExpenseExtraction["status"] | "uploaded";
  extraction?: ExpenseExtraction | null;
  resolution?: ReceiptResolution | null;
  duplicateWarning?: DuplicateExpenseWarning | null;
  previewAvailableRemotely?: boolean;
  expiresAt: string;
};
export type ExpenseImportSourceFile = {
  id: string;
  batchIndex: number;
  originalFilename: string;
  contentType: string;
  sizeBytes: number;
  status: ExpenseExtraction["status"] | "uploaded";
  candidateCount: number;
  errorCode: string;
  errorMessage: string;
  canRemove: boolean;
};
export type ExpenseImportCandidateStatus =
  | "review"
  | "imported"
  | "excluded"
  | "duplicate";
export type ExpenseImportCandidate = {
  id: string;
  sourceImportId: string;
  sourceFilename: string;
  sourceIndex: number;
  sourcePages: number[];
  groupingConfidence: number;
  warnings: string[];
  status: ExpenseImportCandidateStatus;
  extractionResult: ReceiptExtractionResult;
  resolution: ReceiptResolution;
  duplicateWarning?: DuplicateExpenseWarning | null;
  expenseId?: string | null;
  duplicateExpenseId?: string | null;
  exclusionReason: string;
};
export type ExpenseImportBatch = {
  id: string;
  status:
    | "uploading"
    | "processing"
    | "ready"
    | "partial"
    | "completed"
    | "failed";
  fileCount: number;
  candidateCount: number;
  importedCount: number;
  excludedCount: number;
  failedFileCount: number;
  files: ExpenseImportSourceFile[];
  candidates: ExpenseImportCandidate[];
  candidateOffset: number;
  candidateLimit: number;
  expiresAt: string;
  completedAt?: string | null;
};
export const expenseImportBatchIsResolved = (batch: ExpenseImportBatch) =>
  batch.candidateCount > 0 &&
  batch.importedCount + batch.excludedCount >= batch.candidateCount;
export type ExpenseAuditChange = {
  field: string;
  label: string;
  before?: string | null;
  after?: string | null;
};
export type ExpenseAuditEvent = {
  id: string;
  action: string;
  note: string;
  changes: ExpenseAuditChange[];
  actor?: ExpenseReference | null;
  createdAt: string;
};
export type ExpenseCorrectionReference = {
  id: string;
  merchantName: string;
  status: ExpenseStatus;
};
export type DuplicateExpenseReason =
  | "same_receipt_file"
  | "same_receipt_number"
  | "same_merchant"
  | "same_merchant_address"
  | "same_date"
  | "same_amount"
  | "same_currency"
  | "same_payment_account"
  | "same_payment_card";
export type DuplicateExpenseMatch = {
  expenseId: string;
  status: ExpenseStatus;
  merchantName: string;
  expenseDate: string;
  amountCents: number;
  currency: string;
  reasons: DuplicateExpenseReason[];
};
export type DuplicateExpenseWarning = {
  severity: "high" | "likely";
  message: string;
  fingerprint: string;
  matches: DuplicateExpenseMatch[];
};
export type ExpenseConfirmationResult = {
  confirmed: boolean;
  expense: Expense;
  duplicateWarning?: DuplicateExpenseWarning | null;
};
export type ExpenseLineItem = {
  inventoryId?: string | null;
  inventoryTitle?: string | null;
  inventoryQuantity?: number | null;
  stockPosted?: boolean;
  product?: { id: string; name: string } | null;
  id: string;
  itemName: string;
  quantity: number | string;
  unitPriceCents: number;
  lineAmountCents?: number | null;
  discountCents?: number | null;
  taxCents?: number | null;
  totalCents: number;
  category?: ExpenseCategory | null;
  effectiveCategory: ExpenseCategory;
  sortOrder: number;
};
export type ExpenseScheduleLineItem = Omit<
  ExpenseLineItem,
  "effectiveCategory"
>;
export type MarketplacePurchase = {
  refundedCashCents?: number;
  netCashCents?: number;
  id: string;
  subtotalCents: number;
  discountCents: number;
  allowanceCents: number;
  allowancePoints: number;
  creditCents: number;
  taxCents: number;
  totalCents: number;
  lines: Array<{ id: string; title: string; entitlementId: string | null; growthLeadId?: string | null; subtotalCents: number; discountCents: number; allowanceCents: number; allowancePoints: number; creditCents: number; totalCents: number; discountDescription: string }>;
};

export type ExpenseProjectOption = { id: string; name: string; active: boolean };
export type ExpenseProjectSummary = Omit<ExpenseProject, "notes" | "categoryReport" | "auditEvents">;
export type ExpenseProject = ExpenseProjectOption & {
  thisWeekCount?: number; thisMonthCount?: number;
  notesPreview?: string; confirmedDate?: string | null; awaitingReviewDate?: string | null;
  notes: string; createdBy: string | null; createdAt: string; updatedAt: string;
  statusTotals: NonNullable<ExpenseListResponse["statusTotals"]>;
  categoryReport: ExpenseCategoryReport[]; auditEvents: ExpenseAuditEvent[];
};

export type Expense = {
  project?: ExpenseProjectOption | null;
  id: string;
  amountCents: number;
  subtotalCents?: number | null;
  shippingCents?: number | null;
  taxCents?: number | null;
  discountCents?: number | null;
  currency: string;
  expenseDate: string;
  scheduleOccurrenceDate?: string | null;
  merchantName: string;
  merchantAddress?: MerchantAddress | null;
  merchantAddressRaw?: string;
  storeNumber?: string;
  receiptNumber?: string;
  category: ExpenseCategory;
  description: string;
  status: ExpenseStatus;
  source: string;
  commissionPaymentId?: string | null;
  paymentMethod: string;
  paymentAccount?: PaymentAccount | null;
  locationId?: string | null;
  machineId?: string | null;
  location?: ExpenseReference | null;
  machine?: ExpenseReference | null;
  searchMatchReasons?: Array<{ label: string; value: string }>;
  scopeLocationCount?: number | null;
  scopeMachineCount?: number | null;
  scheduleId?: string | null;
  schedule?: ExpenseSchedule | null;
  recurringCalculation?: ExpenseRecurringCalculation | null;
  vendistriCharge?: {
    id: string;
    chargeType: "subscription_cycle" | "machine_activation" | "marketplace";
    marketplacePurchase?: MarketplacePurchase | null;
    billingCycleStart?: string | null;
    billingCycleEnd?: string | null;
  } | null;
  linkedCollectionTaskId?: string | null;
  linkedRefundTaskId?: string | null;
  lineItems: ExpenseLineItem[];
  lineItemAllocation: {
    subtotalCents: number;
    totalCents: number;
    taxAllocatedCents: number;
    taxUnallocatedCents?: number | null;
    taxComplete: boolean;
    discountAllocatedCents: number;
    discountUnallocatedCents?: number | null;
    discountComplete: boolean;
  };
  lineItemWarnings: string[];
  lineItemErrors: string[];
  duplicateWarning?: DuplicateExpenseWarning | null;
  attachments?: ExpenseAttachment[];
  shipments?: ExpenseShipment[];
  archivedShipments?: ExpenseShipment[];
  extraction?: ExpenseExtraction | null;
  auditEvents?: ExpenseAuditEvent[];
  createdBy?: string | null;
  confirmedBy?: string | null;
  confirmedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  correctionOf?: ExpenseCorrectionReference | null;
  correctedBy?: ExpenseCorrectionReference | null;
};

export type MerchantAddress = {
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
};
export type ExpenseMerchantDetails = {
  merchantKey: string;
  merchantName: string;
  merchantAddress: MerchantAddress | null;
  storeNumber: string;
  auditEvents: ExpenseAuditEvent[];
};
export type ExpenseListResponse = {
  items: Expense[];
  nextCursor: string | null;
  totalCount?: number | null;
  statusTotals?: Array<{
    status: ExpenseStatus;
    count: number;
    totals: MoneyTotal[];
  }> | null;
};
export type ExpenseMerchantReport = {
  merchantKey: string;
  merchantName: string;
  totals: MoneyTotal[];
  confirmedTotals: MoneyTotal[];
  awaitingReviewTotals: MoneyTotal[];
  expenseCount: number;
  draftCount: number;
  confirmedCount: number;
  rejectedCount: number;
  voidedCount: number;
  awaitingReviewDate: string | null;
  firstExpenseDate: string | null;
  lastExpenseDate: string | null;
};
export type ExpenseMerchantReportPage = {
  items: ExpenseMerchantReport[];
  nextCursor: string | null;
  totalCount: number;
};
export type ExpenseSummary = {
  expenseCount: number;
  thisWeekTotals: MoneyTotal[];
  previousWeekToDateTotals: MoneyTotal[];
  thisWeekCount: number;
  thisMonthTotals: MoneyTotal[];
  previousMonthToDateTotals: MoneyTotal[];
  thisMonthAssignedTotals: MoneyTotal[];
  previousMonthToDateAssignedTotals: MoneyTotal[];
  thisMonthCount: number;
  needsReviewTotals: MoneyTotal[];
  needsReviewCount: number;
  recurringUpcomingTotals: MoneyTotal[];
  recurringUpcomingCount: number;
  recurringAwaitingReviewTotals: MoneyTotal[];
  recurringAwaitingReviewCount: number;
  recurringNextDueDate?: string | null;
  recurringNextDueScheduleCount: number;
  recurringThisMonthTotals: MoneyTotal[];
  recurringPreviousMonthToDateTotals: MoneyTotal[];
  recurringThisMonthScheduleCount: number;
  recurringActiveScheduleCount: number;
  recurringPausedScheduleCount: number;
  recurringPendingScheduleCount: number;
  weekComparison: ExpenseComparisonPeriod;
  monthComparison: ExpenseComparisonPeriod;
};
export type ExpenseComparisonPeriod = {
  currentStart: string;
  currentEnd: string;
  previousStart: string;
  previousEnd: string;
};
export type ExpensePeriodReport = {
  entityId?: string | null;
  status?: ExpenseStatus;
  period: string;
  totals: MoneyTotal[];
  expenseCount: number;
};
export type ExpenseCategoryReport = {
  categoryId: string;
  categoryName: string;
  totals: MoneyTotal[];
  previousTotals?: MoneyTotal[];
  lineItemCount: number;
  confirmedCount: number;
  awaitingReviewTotals: MoneyTotal[];
  awaitingReviewCount: number;
  awaitingReviewDate: string | null;
  firstExpenseDate: string | null;
  lastExpenseDate: string | null;
  rejectedCount?: number;
  voidedCount?: number;
};
export type ExpensePaymentAccountReport = {
  paymentAccountId: string | null;
  paymentAccountLabel: string;
  unassigned: boolean;
  totals: MoneyTotal[];
  previousTotals?: MoneyTotal[];
  expenseCount: number;
  confirmedCount: number;
  awaitingReviewTotals: MoneyTotal[];
  awaitingReviewCount: number;
  awaitingReviewDate: string | null;
  firstExpenseDate: string | null;
  lastExpenseDate: string | null;
  draftCount?: number;
  rejectedCount?: number;
  voidedCount?: number;
};
export type ExpenseSchedule = {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  category: ExpenseCategory;
  frequency: ExpenseScheduleFrequency;
  interval: number;
  nextDueDate: string;
  calculationPeriodStart?: string | null;
  active: boolean;
  requiresExpenseConfirmation: boolean;
  merchantName: string;
  merchantAddress?: MerchantAddress | null;
  merchantAddressRaw?: string;
  storeNumber?: string;
  description: string;
  calculationType: ExpenseScheduleCalculationType;
  fixedAmountCents?: number | null;
  shippingCents?: number;
  rateBasisPoints?: number | null;
  currency: string;
  paymentMethod: string;
  paymentAccount?: PaymentAccount | null;
  locationId?: string | null;
  machineId?: string | null;
  telemetryId?: string | null;
  lineItems: ExpenseScheduleLineItem[];
  rateGroups: ExpenseScheduleRateGroup[];
  historySummary?: ExpenseScheduleHistorySummary | null;
  generationIssue?: {
    code: "expense_generation_failed";
    occurrenceDate: string;
    failedAt: string;
  } | null;
};

export type ExpenseScheduleBackfillOccurrence = {
  expenseDate: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  amountCents: number;
  action: "create" | "skip" | "zero" | "unavailable";
  existingExpenseId?: string | null;
  existingExpenseStatus?: ExpenseStatus | null;
  machineCount?: number | null;
  partialMachineCount?: number | null;
  unavailableReason?: string | null;
  warnings?: string[];
};

export type ExpenseScheduleBackfillPreview = {
  occurrences: ExpenseScheduleBackfillOccurrence[];
  createCount: number;
  skipCount: number;
  zeroCount?: number;
  unavailableCount?: number;
  disclosure?: string | null;
  previewToken?: string | null;
};

export type ExpenseScheduleBackfillResult = {
  createdCount: number;
  draftCount: number;
  confirmedCount: number;
  excludedCount: number;
  existingSkippedCount: number;
  skippedCount: number;
  createdExpenseIds: string[];
};

export type ExpenseScheduleHistorySummary = {
  historyCount: number;
  firstExpenseDate?: string | null;
  lastExpenseDate?: string | null;
  generatedCents: number;
  generatedCount: number;
  confirmedCents: number;
  confirmedCount: number;
  confirmedThisMonthCount?: number;
  draftThisMonthCount?: number;
  rejectedThisMonthCount?: number;
  voidedThisMonthCount?: number;
  awaitingReviewCents: number;
  awaitingReviewCount: number;
};

export type ExpenseScheduleFrequency =
  | "weekly"
  | "monthly"
  | "quarterly"
  | "yearly"
  | "custom";

export type ExpenseScheduleCalculationType =
  | "fixed"
  | "provider";

export type ExpenseScheduleRoundingMode =
  | "per_transaction"
  | "period_total";

export type ExpenseScheduleRateComponentType =
  | "fixed_period"
  | "percentage_of_sales"
  | "percentage_of_card_sales"
  | "percentage_of_cash_sales"
  | "per_transaction"
  | "per_card_transaction"
  | "per_cash_transaction"
  | "per_device";

export type ExpenseScheduleRateComponent = {
  id: string;
  type: ExpenseScheduleRateComponentType;
  rateBasisPoints?: number | null;
  amountCents?: number | null;
};

export type ExpenseScheduleRateGroup = {
  id: string;
  name: string;
  roundingMode: ExpenseScheduleRoundingMode;
  machineIds: string[];
  components: ExpenseScheduleRateComponent[];
};

export type ExpenseRecurringCalculationComponent = {
  type: ExpenseScheduleRateComponentType;
  rateBasisPoints?: number | null;
  amountCents?: number | null;
  baseCents?: number | null;
  transactionCount?: number | null;
  totalCents: number;
};

export type ExpenseRecurringCalculationLocation = {
  locationId: string | null;
  cardSalesCents: number;
  cashSalesCents: number;
  coinSalesCents: number;
  transactionCount: number;
};

export type ExpenseRecurringCalculationMachine = {
  machineId: string;
  machineName: string;
  active: boolean;
  totalCents: number;
  locations: ExpenseRecurringCalculationLocation[];
  components: ExpenseRecurringCalculationComponent[];
};

export type ExpenseRecurringCalculationGroup = {
  groupId: string;
  name: string;
  roundingMode: ExpenseScheduleRoundingMode;
  totalCents: number;
  fixedComponents: ExpenseRecurringCalculationComponent[];
  machines: ExpenseRecurringCalculationMachine[];
};

export type ExpenseRecurringCalculation = {
  calculationId?: string;
  telemetryId: string;
  periodStart: string;
  periodEnd: string;
  totalCents: number;
  groups: ExpenseRecurringCalculationGroup[];
  auditTotals: {
    cashSalesCents: number;
    cardSalesCents: number;
    coinSalesCents: number;
    count: number;
    cardCount: number;
    cashCount: number;
    cardSurchargeCents: number;
    cardFeeBaseCents: number;
  };
  proceeds?: {
    basis: "card" | "sales";
    grossCents: number;
    feeCents: number;
    netCents: number;
  } | null;
};
